import { useState, useEffect, useCallback, useRef } from 'react';
import customersService from '../services/customersService';
import i18n from '../i18n';
import { useMessageAlert } from '../components/common/MessageAlert';

// Default number of customers per page
const DEFAULT_PAGE_SIZE = 20;

/**
 * Map API customer object to UI-friendly shape
 * Ensures consistent field names and default values for the UI layer
 */
const mapApiToCustomer = (item = {}) => ({
  id: item.id ?? item.customer_id ?? null,
  ownerName: item.owner_name || '',
  companyName: item.company_name || '',
  gstNumber: item.gst_number || '',
  contactPerson: item.contact_person || '',
  customerType: item.customer_type || '',
  address1: item.address_1 || '',
  address2: item.address_2 || '',
  state: item.state || '',
  city: item.city || '',
  country: item.country || '',
  zipCode: item.zip_code || '',
  deliveryLocation: item.delivery_location || '',
  email: item.email || '',
  phoneNumber1: item.phone_number_1 || '',
  phoneNumber2: item.phone_number_2 || '',
  tallyName: item.tally_name || '',
  creditPeriod:
    item.credit_period === null || item.credit_period === undefined
      ? ''
      : Number(item.credit_period),
  isActive: item.is_active ?? true,
  sisterCompany: item.sister_company ?? false,
  createdAt: item.created_at || '',
  updatedAt: item.updated_at || '',
});

/**
 * Map UI customer object to API payload shape
 * Converts UI field names back to the API expected keys
 */
const mapCustomerToApi = (customer = {}) => ({
  owner_name: customer.ownerName || '',
  company_name: customer.companyName || '',
  gst_number: customer.gstNumber || '',
  contact_person: customer.contactPerson || '',
  customer_type: customer.customerType || '',
  address_1: customer.address1 || '',
  address_2: customer.address2 || '',
  state: customer.state || '',
  city: customer.city || '',
  country: customer.country || '',
  zip_code: customer.zipCode || '',
  delivery_location: customer.deliveryLocation || '',
  email: customer.email || '',
  phone_number_1: customer.phoneNumber1 || '',
  phone_number_2: customer.phoneNumber2 || '',
  tally_name: customer.tallyName || '',
  credit_period:
    customer.creditPeriod === '' || customer.creditPeriod === null || customer.creditPeriod === undefined
      ? 0
      : Number(customer.creditPeriod),
  is_active: customer.isActive ?? true,
  sister_company: customer.sisterCompany ?? false,
});

/**
 * Custom hook to manage customers list, pagination, filters, and CRUD operations.
 * - Handles deduplication of fetches and optimistic updates for activate/deactivate.
 */
export const useCustomers = (initialOptions = {}) => {
  const {
    autoFetch = true,
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = {},
  } = initialOptions;

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCustomers, setSelectedCustomers] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [filters, setFilters] = useState({
    search: '',
    customerType: '',
    state: '',
    city: '',
    isActive: true,
    ...initialFilters,
  });

  const currentPageRef = useRef(currentPage);
  const filtersRef = useRef(filters);
  const pageSizeRef = useRef(pageSize);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  const lastFetchSignatureRef = useRef(null);
  const inFlightSignatureRef = useRef(null);

  const [importResult, setImportResult] = useState(null);
  const { showCreate, showUpdate, showDelete } = useMessageAlert();

  const fetchCustomers = useCallback(async (options = {}) => {
    const page = options.page ?? currentPageRef.current;

    const params = {
      page,
      limit: pageSizeRef.current,
      search: filtersRef.current.search,
      customer_type: filtersRef.current.customerType,
      state: filtersRef.current.state,
      city: filtersRef.current.city,
      is_active: filtersRef.current.isActive,
      ...options.params,
    };

    const signature = JSON.stringify(params);

    if (!options.force) {
      if (inFlightSignatureRef.current === signature) {
        return;
      }

      const lastSignature = lastFetchSignatureRef.current;
      if (lastSignature && lastSignature.signature === signature) {
        const elapsed = Date.now() - lastSignature.timestamp;
        if (elapsed < 200) {
          return;
        }
      }
    }

    inFlightSignatureRef.current = signature;
    setLoading(true);
    setError(null);

    try {
      const result = await customersService.getCustomers(params);

      if (result.success) {
        const mappedCustomers = Array.isArray(result.data)
          ? result.data.map(mapApiToCustomer)
          : [];
        setCustomers(mappedCustomers);
        setTotalCount(result.total ?? mappedCustomers.length);
        const calculatedTotalPages = Math.max(1, Math.ceil((result.total ?? mappedCustomers.length) / pageSizeRef.current));
        setTotalPages(calculatedTotalPages);

        if (options.page && options.page !== currentPageRef.current) {
          setCurrentPage(options.page);
        }
      } else {
        setCustomers([]);
        setError(result.error);
      }
    } catch (err) {
      setCustomers([]);
      setError(i18n.t('hookErrors.customers.fetchList'));
    } finally {
      lastFetchSignatureRef.current = { signature, timestamp: Date.now() };
      inFlightSignatureRef.current = null;
      setLoading(false);
    }
  }, []);

  const getCustomerById = useCallback(async (customerId, options = {}) => {
    const { withLoading = false } = options;
    if (withLoading) {
      setLoading(true);
    }
    setError(null);

    try {
      const result = await customersService.getCustomerById(customerId);
      if (result.success) {
        return mapApiToCustomer(result.data);
      }
      setError(result.error);
      return null;
    } catch (err) {
      setError(i18n.t('hookErrors.customers.fetchOne'));
      return null;
    } finally {
      if (withLoading) {
        setLoading(false);
      }
    }
  }, []);

  const createCustomer = useCallback(async (customerData) => {
    // Don't toggle list-level `loading` on mutations — it drives the table
    // skeleton behind the modal and flickers on every save (visible on failure
    // with the modal open). On success we refetch, which sets `loading` itself.
    setError(null);
    try {
      const payload = mapCustomerToApi(customerData);
      const result = await customersService.createCustomer(payload);
      if (result.success) {
  await fetchCustomers();
  const nameCreate = customerData?.companyName || customerData?.ownerName || i18n.t('customers.details.header.fallbackTitle') || 'Customer';
  showCreate(i18n.t('customers.notifications.created', { name: nameCreate }));
        return { success: true, data: result.data };
      }
      setError(result.error);
      return { success: false, error: result.error };
    } catch (err) {
      const message = 'Failed to create customer';
      setError(message);
      return { success: false, error: message };
    }
  }, [fetchCustomers, showCreate]);

  const updateCustomer = useCallback(async (customerId, customerData) => {
    setError(null);
    try {
      const payload = mapCustomerToApi(customerData);
      const result = await customersService.updateCustomer(customerId, payload);
      if (result.success) {
        const updatedCustomer = result.data ? mapApiToCustomer(result.data) : { ...customerData, id: customerId };
        setCustomers((prev) =>
          prev.map((customer) => (customer.id === customerId ? { ...customer, ...updatedCustomer } : customer))
        );
  const nameUpdate = updatedCustomer?.companyName || updatedCustomer?.ownerName || i18n.t('customers.details.header.fallbackTitle') || 'Customer';
  showUpdate(i18n.t('customers.notifications.updated', { name: nameUpdate }));
        return { success: true };
      }
      setError(result.error);
      return { success: false, error: result.error };
    } catch (err) {
      const message = 'Failed to update customer';
      setError(message);
      return { success: false, error: message };
    }
  }, [showUpdate]);

  const deleteCustomer = useCallback(async (customerId) => {
    setError(null);
    try {
      // derive name from local state before deletion
      const customerToDelete = customers.find(c => c.id === customerId);
  const customerName = customerToDelete?.companyName || customerToDelete?.ownerName || `Customer ${customerId}`;
      const result = await customersService.deleteCustomer(customerId);
      if (result.success) {
        setCustomers((prev) => prev.filter((customer) => customer.id !== customerId));
        setSelectedCustomers((prev) => prev.filter((id) => id !== customerId));
        setTotalCount((prev) => Math.max(0, prev - 1));
  showDelete(i18n.t('customers.notifications.deleted', { name: customerName }));
        return { success: true };
      }
      setError(result.error);
      return { success: false, error: result.error };
    } catch (err) {
      const message = 'Failed to delete customer';
      setError(message);
      return { success: false, error: message };
    }
  }, [showDelete, customers]);

  const bulkDeleteCustomers = useCallback(async (customerIds = null) => {
    const idsToDelete = customerIds || selectedCustomers;
    if (!idsToDelete?.length) {
      return { success: false, error: i18n.t('hookErrors.noSelection.customers') };
    }

    setError(null);
    try {
      const promises = idsToDelete.map((id) => customersService.deleteCustomer(id));
      const results = await Promise.all(promises);
      const hasFailure = results.some((result) => !result.success);

      if (!hasFailure) {
        setCustomers((prev) => prev.filter((customer) => !idsToDelete.includes(customer.id)));
        setSelectedCustomers([]);
        setTotalCount((prev) => Math.max(0, prev - idsToDelete.length));
  const bulkMsg = idsToDelete.length > 1 ? i18n.t('customers.notifications.bulkDeleted', { count: String(idsToDelete.length) }) : i18n.t('customers.notifications.bulkDeleted');
  showDelete(bulkMsg || `${idsToDelete.length} customer${idsToDelete.length > 1 ? 's' : ''} ${idsToDelete.length > 1 ? 'have' : 'has'} been deleted successfully`);
        return { success: true };
      }

      const firstError = results.find((result) => !result.success)?.error;
      setError(firstError);
      return { success: false, error: firstError };
    } catch (err) {
      const message = 'Failed to delete customers';
      setError(message);
      return { success: false, error: message };
    }
  }, [selectedCustomers, showDelete]);

  // Activate a customer (optimistic update)
  const activateCustomer = useCallback(async (customerId) => {
    if (!customerId && customerId !== 0) {
      const msg = 'activateCustomer called without a valid id';
      console.error(msg, { customerId });
      return { success: false, error: msg };
    }

    setError(null);
    let prevSnapshot;
    setCustomers((prev) => {
      prevSnapshot = prev;
      return prev.map((customer) => (customer.id === customerId ? { ...customer, isActive: true } : customer));
    });

    try {
      setLoading(true);
      const result = await customersService.activateCustomer(customerId);
      if (result.success) {
        const updatedCustomer = result.data ? mapApiToCustomer(result.data) : null;
        if (updatedCustomer) {
          setCustomers((prev) => prev.map((customer) => (customer.id === customerId ? { ...customer, ...updatedCustomer } : customer)));
        }
        // Refresh the full list to ensure UI has latest data
        try {
          await fetchCustomers({ force: true });
        } catch (e) {
          // ignore fetch errors here; fetchCustomers sets its own error state
        }
          // show update message (prefer API name, fallback to local list)
            try {
            const displayName = updatedCustomer?.companyName || updatedCustomer?.ownerName || customers.find(c => c.id === customerId)?.companyName || customers.find(c => c.id === customerId)?.ownerName || `Customer ${customerId}`;
            showUpdate(i18n.t('customers.notifications.statusUpdated', { name: displayName }));
          } catch (e) {}
        return { success: true, data: updatedCustomer };
      }

      // revert optimistic update on API error
      if (prevSnapshot) setCustomers(prevSnapshot);
      setError(result.error);
      return { success: false, error: result.error };
    } catch (err) {
      console.error('Failed to activate customer:', err);
      if (prevSnapshot) setCustomers(prevSnapshot);
      const message = 'Failed to activate customer';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [fetchCustomers, showUpdate, customers]);

  // Deactivate a customer (optimistic update)
  const deactivateCustomer = useCallback(async (customerId) => {
    if (!customerId && customerId !== 0) {
      const msg = 'deactivateCustomer called without a valid id';
      console.error(msg, { customerId });
      return { success: false, error: msg };
    }

    setError(null);
    let prevSnapshot;
    setCustomers((prev) => {
      prevSnapshot = prev;
      return prev.map((customer) => (customer.id === customerId ? { ...customer, isActive: false } : customer));
    });

    try {
      setLoading(true);
      const result = await customersService.deactivateCustomer(customerId);
      if (result.success) {
        const updatedCustomer = result.data ? mapApiToCustomer(result.data) : null;
        if (updatedCustomer) {
          setCustomers((prev) => prev.map((customer) => (customer.id === customerId ? { ...customer, ...updatedCustomer } : customer)));
        }
        // Refresh the full list to ensure UI has latest data
        try {
          await fetchCustomers({ force: true });
        } catch (e) {
          // ignore fetch errors here; fetchCustomers sets its own error state
        }
          // show update message (prefer API name, fallback to local list)
            try {
            const displayName = updatedCustomer?.companyName || updatedCustomer?.ownerName || customers.find(c => c.id === customerId)?.companyName || customers.find(c => c.id === customerId)?.ownerName || `Customer ${customerId}`;
            showUpdate(i18n.t('customers.notifications.statusUpdated', { name: displayName }));
          } catch (e) {}
        return { success: true, data: updatedCustomer };
      }

      // revert optimistic update on API error
      if (prevSnapshot) setCustomers(prevSnapshot);
      setError(result.error);
      return { success: false, error: result.error };
    } catch (err) {
      console.error('Failed to deactivate customer:', err);
      if (prevSnapshot) setCustomers(prevSnapshot);
      const message = 'Failed to deactivate customer';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [fetchCustomers, showUpdate, customers]);

  // Backwards-compatible wrapper: toggles via activate/deactivate
  const toggleCustomerStatus = useCallback(async (customerId, isActive) => {
    return isActive ? activateCustomer(customerId) : deactivateCustomer(customerId);
  }, [activateCustomer, deactivateCustomer]);

  const importCustomers = useCallback(async (file) => {
    setLoading(true);
    setError(null);
    setImportResult(null);
    try {
      const result = await customersService.importCustomersFromExcel(file);
      if (result.success) {
        await fetchCustomers();
        setImportResult({ success: true, data: result.data });
        return { success: true };
      }
      setError(result.error);
      setImportResult({ success: false, error: result.error });
      return { success: false, error: result.error };
    } catch (err) {
      const message = 'Failed to import customers';
      setError(message);
      setImportResult({ success: false, error: message });
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [fetchCustomers]);

  const exportCustomers = useCallback(async () => {
    const currentFilters = filtersRef.current;
    const params = {
      search: currentFilters.search,
      customer_type: currentFilters.customerType,
      state: currentFilters.state,
      is_active: currentFilters.isActive,
    };

    const result = await customersService.exportCustomersToExcel(params);
    if (!result.success) {
      return { success: false, error: result.error || i18n.t('customers.actions.alerts.noDataToExport') };
    }

    // Derive filename from Content-Disposition header, fall back to default
    const disposition = result.headers?.['content-disposition'] || '';
    const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    const filename = match ? match[1].replace(/['"]/g, '') : i18n.t('customers.export.filename');

    const blobUrl = URL.createObjectURL(result.data);
    const anchor = document.createElement('a');
    anchor.href = blobUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(blobUrl);

    return { success: true };
  }, []);

  const updateFilters = useCallback((newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setCurrentPage(1);
  }, []);

  const goToPage = useCallback((page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      setCurrentPage(page);
    }
  }, [currentPage, totalPages]);

  const nextPage = useCallback(() => {
    goToPage(currentPage + 1);
  }, [currentPage, goToPage]);

  const prevPage = useCallback(() => {
    goToPage(currentPage - 1);
  }, [currentPage, goToPage]);

  const selectCustomer = useCallback((customerId) => {
    setSelectedCustomers((prev) =>
      prev.includes(customerId)
        ? prev.filter((id) => id !== customerId)
        : [...prev, customerId]
    );
  }, []);

  const selectAllCustomers = useCallback((shouldSelectAll = true) => {
    if (shouldSelectAll) {
      setSelectedCustomers(customers.map((customer) => customer.id));
    } else {
      setSelectedCustomers([]);
    }
  }, [customers]);

  const clearSelection = useCallback(() => {
    setSelectedCustomers([]);
  }, []);

  const isCustomerSelected = useCallback(
    (customerId) => selectedCustomers.includes(customerId),
    [selectedCustomers]
  );

  const isAllSelected = useCallback(
    () => customers.length > 0 && selectedCustomers.length === customers.length,
    [customers, selectedCustomers]
  );

  const hasCustomers = customers.length > 0;
  const hasSelection = selectedCustomers.length > 0;
  const selectionCount = selectedCustomers.length;

  // Single effect to handle both filter changes and page changes
  // When filters change, we reset to page 1 via updateFilters
  useEffect(() => {
    if (!autoFetch) return;
    fetchCustomers({ page: currentPage });
  }, [filters, currentPage, fetchCustomers, autoFetch]);

  return {
    customers,
    loading,
    error,
    selectedCustomers,
    currentPage,
    totalPages,
    totalCount,
    filters,
    importResult,
    hasCustomers,
    hasSelection,
    selectionCount,
    fetchCustomers,
    getCustomerById,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    bulkDeleteCustomers,
  toggleCustomerStatus,
  activateCustomer,
  deactivateCustomer,
    importCustomers,
    exportCustomers,
    updateFilters,
    goToPage,
    nextPage,
    prevPage,
    selectCustomer,
    selectAllCustomers,
    clearSelection,
    isCustomerSelected,
    isAllSelected,
  };
};
